import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import { GripHorizontal } from 'lucide-react'

/**
 * Generic grid-resize handle bound to a widget's wrapper-class string
 * (`dataLocation`). Dragging it sets the widget width by snapping to the parent
 * 12-column grid : it rewrites the `col-span-*` / `@5xl:col-span-*` tokens of the
 * class string to `col-span-12 @5xl:col-span-{n}` (n in [min, 12]) and, on release,
 * fires the YAML `actions:` via a custom DOM event (default `resizecommit`) so
 * the page can persist the new width declaratively (stringifyData + submitData).
 *
 * Why a primitive : translating a pointer drag into a snapped column count and
 * splicing Tailwind span classes is stateful + DOM-measuring → impossible in
 * declarative YAML. Preserves every non-span class (e.g. `card`). Runtime UnoCSS
 * generates the `col-span-{n}` utilities on the fly (they need not exist at build).
 *
 * YAML :
 *   - type: ResizeHandle
 *     dataLocation: ~.wrapperClass
 *     min: 3
 *     actions:
 *       - { what: stringifyData, on: resizecommit, source: ~~.widgets, target: ~~.saveData.layout }
 *       - { what: submitData, on: resizecommit, url: ~~.submissionUrl, ... }
 */
// Le responsive d'un bloc se mesure sur le CONTENEUR (`@5xl:` = 1024 px sur
// `#builder-canvas`), jamais sur la fenêtre : un `lg:` échapperait à l'aperçu par
// appareil du builder. La forme historique reste RECONNUE pour être retirée des
// classes déjà enregistrées, elle n'est plus écrite.
const SPAN_RE = /\b(?:(?:@5xl|lg):)?col-span-\d+\b/g

export const ResizeHandle = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const handleRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: '',
    globalDataContext,
    templateContext,
  })
  const min = Math.max(1, parseInt(resolve(props.min), 10) || 3)
  const commitEvent = resolve(props.commitEvent) || 'resizecommit'

  // Gesture refs. To keep the drag SMOOTH we do NOT write the store on every
  // move (that would re-render the whole reactive-json tree + redraw every
  // chart). Instead we preview the width by setting the widget cell's class
  // DIRECTLY in the DOM during the drag, and commit to the store + fire the save
  // event ONCE on release.
  const baseRef = useRef('')   // non-span class base, captured at drag start
  const cellRef = useRef(null) // the grid cell (widget wrapper) being resized
  const spanRef = useRef(null) // last previewed column count

  const columnsFor = (clientX) => {
    const grid = handleRef.current?.closest('.grid')
    if (!grid) return null
    const rect = grid.getBoundingClientRect()
    if (rect.width <= 0) return null
    const ratio = (clientX - rect.left) / rect.width
    const n = Math.round(ratio * 12)
    return Math.min(12, Math.max(min, n))
  }

  const classFor = (n) => `${baseRef.current} col-span-12 @5xl:col-span-${n}`.trim()

  const onPointerMove = (e) => {
    const n = columnsFor(e.clientX)
    if (n == null || n === spanRef.current) return
    spanRef.current = n
    if (cellRef.current) cellRef.current.className = classFor(n) // preview only, no store write
  }

  const onPointerUp = () => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('mousemove', onPointerMove)
    window.removeEventListener('mouseup', onPointerUp)
    if (spanRef.current != null) {
      // Commit the final width to the store (one re-render), then persist.
      globalDataContext.updateData(classFor(spanRef.current), formDataPath)
      handleRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }))
    }
  }

  const onPointerDown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    // Locate the grid cell (widget wrapper = direct child of `.grid`).
    const grid = handleRef.current?.closest('.grid')
    let cell = handleRef.current
    while (cell && cell.parentElement && cell.parentElement !== grid) cell = cell.parentElement
    cellRef.current = cell && cell.parentElement === grid ? cell : null
    baseRef.current = String((cellRef.current?.className) || formData || '')
      .replace(SPAN_RE, '').replace(/\s+/g, ' ').trim()
    spanRef.current = null
    // Pointer-capture is deliberately NOT used : it retargets move events to the
    // handle and can starve the window listeners (and Playwright). Listen on both
    // pointer + mouse for maximum compatibility.
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('mousemove', onPointerMove)
    window.addEventListener('mouseup', onPointerUp)
  }

  return (
    <ActionDependant {...props} attributesHolderRef={handleRef}>
      <button
        ref={handleRef}
        type="button"
        onPointerDown={onPointerDown}
        title="Redimensionner (largeur)"
        aria-label="Redimensionner la largeur du widget"
        className="cursor-ew-resize text-slate-300 hover:text-iris-500 touch-none px-1"
      >
        <GripHorizontal size={16} />
      </button>
    </ActionDependant>
  )
}
